export const MAP_ANIMATION_OPERATORS = [
  { value: '=',       label: 'Equal To' },
  { value: '!=',      label: 'Not Equal To' },
  { value: '<',       label: 'Less Than' },
  { value: '<=',      label: 'Less Than or Equal To' },
  { value: '>',       label: 'Greater Than' },
  { value: '>=',      label: 'Greater Than or Equal To' },
  { value: 'between', label: 'Between' },
];

export const MAP_ANIMATION_PRESETS = ['none', 'pulse', 'bounce', 'glow', 'ping', 'shake'];

export function parseRuleNumber(rawValue) {
  if (rawValue === '' || rawValue == null) return null;
  const parsed = Number.parseFloat(String(rawValue).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

export function isStringValue(value) {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') return false;
  return !Number.isFinite(Number(trimmed));
}

export function normalizeHexColor(value, fallback = '#3b82f6') {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return /^#[0-9a-f]{6}$/.test(normalized) ? normalized : fallback;
}

export function createDefaultMapAnimationRule() {
  return {
    operator: '>',
    value: '',
    min: '',
    max: '',
    animation: 'none',
    color: '#3b82f6',
  };
}

export function toEditableMapAnimationRule(rule) {
  if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
    return createDefaultMapAnimationRule();
  }
  const operator = MAP_ANIMATION_OPERATORS.some((op) => op.value === rule.operator) ? rule.operator : '>';
  const animation = MAP_ANIMATION_PRESETS.includes(rule.animation) ? rule.animation : 'none';
  return {
    operator,
    value: rule.value == null ? '' : String(rule.value),
    min: rule.min == null ? '' : String(rule.min),
    max: rule.max == null ? '' : String(rule.max),
    animation,
    color: normalizeHexColor(rule.color, '#3b82f6'),
  };
}

export function createEmptyQuickMetric() {
  return {
    source: '',
    field: '',
    friendlyName: '',
    unit: '',
    mapAnimationRule: createDefaultMapAnimationRule(),
  };
}

export function toMetricLabel(field = '') {
  return String(field || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function flattenTimeseriesStyleSpecs(specifications) {
  if (!specifications || typeof specifications !== 'object' || Array.isArray(specifications)) return [];
  const flattened = [];
  for (const [source, lines] of Object.entries(specifications)) {
    if (!Array.isArray(lines)) continue;
    for (const line of lines) {
      if (!line?.field) continue;
      flattened.push({
        source,
        field: line.field,
        friendlyName: line.friendlyName || line.label || '',
        unit: line.unit || '',
      });
    }
  }
  return flattened;
}

function dottedSnapshotSpecs(rawSpecs) {
  if (!rawSpecs || typeof rawSpecs !== 'object' || Array.isArray(rawSpecs)) return [];
  const specs = [];
  for (const key of Object.keys(rawSpecs)) {
    if (!key.includes('.')) continue;
    const splitIdx = key.indexOf('.');
    const source = key.slice(0, splitIdx).trim();
    const field = key.slice(splitIdx + 1).trim();
    if (!source || !field) continue;
    specs.push({
      source,
      field,
      friendlyName: toMetricLabel(field),
      unit: '',
      mapAnimationRule: createDefaultMapAnimationRule(),
    });
  }
  return specs;
}

export function normalizeQuickMetricSpecs(rawSpecs) {
  if (!Array.isArray(rawSpecs)) {
    return dottedSnapshotSpecs(rawSpecs);
  }
  return rawSpecs
    .map((spec) => ({
      source: typeof spec === 'string'
        ? (spec.includes('.') ? spec.slice(0, spec.indexOf('.')).trim() : '')
        : (typeof spec?.source === 'string' ? spec.source : ''),
      field: typeof spec === 'string'
        ? (spec.includes('.') ? spec.slice(spec.indexOf('.') + 1).trim() : '')
        : (typeof spec?.field === 'string' ? spec.field : ''),
      friendlyName: typeof spec?.friendlyName === 'string'
        ? spec.friendlyName
        : (typeof spec === 'string'
          ? toMetricLabel(spec.includes('.') ? spec.slice(spec.indexOf('.') + 1) : spec)
          : ''),
      unit: typeof spec?.unit === 'string' ? spec.unit : '',
      mapAnimationRule: toEditableMapAnimationRule(spec?.mapAnimationRule),
    }))
    .filter((spec) => spec.field);
}

function intervalForRule(rule) {
  if (rule.operator === 'between') {
    return { min: rule.min, max: rule.max, includeMin: true, includeMax: true };
  }
  if (rule.operator === '=') {
    return { min: rule.value, max: rule.value, includeMin: true, includeMax: true };
  }
  if (rule.operator === '<') {
    return { min: Number.NEGATIVE_INFINITY, max: rule.value, includeMin: false, includeMax: false };
  }
  if (rule.operator === '<=') {
    return { min: Number.NEGATIVE_INFINITY, max: rule.value, includeMin: false, includeMax: true };
  }
  if (rule.operator === '>') {
    return { min: rule.value, max: Number.POSITIVE_INFINITY, includeMin: false, includeMax: false };
  }
  if (rule.operator === '>=') {
    return { min: rule.value, max: Number.POSITIVE_INFINITY, includeMin: true, includeMax: false };
  }
  return null;
}

function intervalsOverlap(a, b) {
  const left = Math.max(a.min, b.min);
  const right = Math.min(a.max, b.max);
  if (left < right) return true;
  if (left > right) return false;
  const aIncludes = (left === a.min) ? a.includeMin : a.includeMax;
  const bIncludes = (left === b.min) ? b.includeMin : b.includeMax;
  return aIncludes && bIncludes;
}

function rulesOverlap(a, b) {
  const aIsStr = (a.operator === '=' || a.operator === '!=') && isStringValue(a.value);
  const bIsStr = (b.operator === '=' || b.operator === '!=') && isStringValue(b.value);
  if (aIsStr || bIsStr) {
    const aVal = String(a.value ?? '');
    const bVal = String(b.value ?? '');
    if (a.operator === '=' && b.operator === '=') return aVal === bVal;
    if (a.operator === '=' && b.operator === '!=') return aVal !== bVal;
    if (a.operator === '!=' && b.operator === '=') return bVal !== aVal;
    return true;
  }
  if (a.operator === '!=' || b.operator === '!=') {
    if (a.operator === '=' && b.operator === '!=') return a.value !== b.value;
    if (b.operator === '=' && a.operator === '!=') return b.value !== a.value;
    return true;
  }
  const ai = intervalForRule(a);
  const bi = intervalForRule(b);
  if (!ai || !bi) return false;
  return intervalsOverlap(ai, bi);
}

export function validateAnimationRuleOverlaps(serializedSpecs) {
  const byFieldKey = new Map();
  serializedSpecs.forEach((spec) => {
    if (!spec?.mapAnimationRule) return;
    const key = `${spec.source || ''}::${spec.field || ''}`;
    if (!byFieldKey.has(key)) byFieldKey.set(key, []);
    byFieldKey.get(key).push(spec.mapAnimationRule);
  });

  for (const [fieldKey, rules] of byFieldKey.entries()) {
    for (let i = 0; i < rules.length; i += 1) {
      for (let j = i + 1; j < rules.length; j += 1) {
        if (rulesOverlap(rules[i], rules[j])) {
          const [source, field] = fieldKey.split('::');
          return `Map animation rules overlap for ${source}.${field}. Adjust thresholds so only one rule can ever match.`;
        }
      }
    }
  }
  return '';
}

export function serializeMapAnimationRule(rule) {
  const editable = toEditableMapAnimationRule(rule);
  if (editable.animation === 'none') return null;
  const color = normalizeHexColor(editable.color, '#3b82f6');

  if (editable.operator === 'between') {
    const min = parseRuleNumber(editable.min);
    const max = parseRuleNumber(editable.max);
    if (min == null || max == null || min > max) return { error: 'Between rules require numeric Min and Max, and Min must be <= Max.' };
    return {
      enabled: true,
      operator: 'between',
      min,
      max,
      animation: editable.animation,
      ...(['glow', 'ping'].includes(editable.animation) ? { color } : {}),
    };
  }

  const valueStr = String(editable.value ?? '').trim();
  if (valueStr === '') return { error: `Operator "${editable.operator}" requires a threshold value.` };

  if (editable.operator === '=' || editable.operator === '!=') {
    const numValue = parseRuleNumber(editable.value);
    const finalValue = numValue !== null ? numValue : valueStr;
    return {
      enabled: true,
      operator: editable.operator,
      value: finalValue,
      animation: editable.animation,
      ...(['glow', 'ping'].includes(editable.animation) ? { color } : {}),
    };
  }

  const value = parseRuleNumber(editable.value);
  if (value == null) return { error: `Operator "${editable.operator}" requires a numeric threshold.` };
  return {
    enabled: true,
    operator: editable.operator,
    value,
    animation: editable.animation,
    ...(['glow', 'ping'].includes(editable.animation) ? { color } : {}),
  };
}

export function isAnimationNone(metric) {
  return toEditableMapAnimationRule(metric?.mapAnimationRule).animation === 'none';
}

export function mapAnimationPreviewClass(metric) {
  const animation = toEditableMapAnimationRule(metric?.mapAnimationRule).animation;
  return animation === 'none' ? '' : `map-animation-preview--${animation}`;
}

export function mapAnimationPreviewStyle(metric) {
  const rule = toEditableMapAnimationRule(metric?.mapAnimationRule);
  return { '--map-preview-glow-color': normalizeHexColor(rule.color, '#3b82f6') };
}

export function availableOperatorsForMetric(metric) {
  if (isStringValue(metric?.mapAnimationRule?.value)) {
    return MAP_ANIMATION_OPERATORS.filter((op) => op.value === '=' || op.value === '!=');
  }
  return MAP_ANIMATION_OPERATORS;
}
